import { handleUnknownMapping } from '@/features/game-window-integration/helpers/generic-helpers';
import { errorsRefs } from '@/features/game-window-integration/providers/aristocrat/error-helpers';
import { dgcErrorRefs } from '@/features/game-window-integration/providers/dgc/error-helpers';
import { errorRefsPragmatic } from '@/features/game-window-integration/providers/pragmatic/error-helpers.ts';

export const ERROR_TYPE = {
  GENERIC: 'catch_all',
  GENERIC_DISMISSABLE: 'generic_dismissable',
  INSUFFICIENT_FUNDS: 'balance_too_low',
  SESSION_EXPIRED: 'session_expired',
  GAME_IN_PROGRESS: 'game_in_progress',
  GAME_STOPPED_WORKING: 'game_stopped_working',
  CONNECTION_LOST: 'connection_lost',
  TOO_MANY_GAMES_OPEN: 'too_many_games_open',
  UNKNOWN: 'unknown_error_nyx',
  FREE_SPIN_DIALOG_START: 'free_spin_dialog_start',
  FREE_SPIN_DIALOG_CHOOSE: 'free_spin_dialog_choose',
  FREE_SPIN_DIALOG_FINISH: 'free_spin_dialog_finish',
} as const;

const PROVIDER_ERRORS = {
  IGT: {
    1025: ERROR_TYPE.INSUFFICIENT_FUNDS,
    1005: ERROR_TYPE.INSUFFICIENT_FUNDS,
    1601: ERROR_TYPE.INSUFFICIENT_FUNDS,
    1033: ERROR_TYPE.INSUFFICIENT_FUNDS,
    1635: ERROR_TYPE.INSUFFICIENT_FUNDS,
    4014: ERROR_TYPE.SESSION_EXPIRED,
    1007: ERROR_TYPE.SESSION_EXPIRED,
    1006: ERROR_TYPE.SESSION_EXPIRED,
    1600: ERROR_TYPE.SESSION_EXPIRED,
    1015: ERROR_TYPE.GAME_IN_PROGRESS,
    1602: ERROR_TYPE.GAME_STOPPED_WORKING,
    1603: ERROR_TYPE.GAME_STOPPED_WORKING,
    1604: ERROR_TYPE.GAME_STOPPED_WORKING,
    1000: ERROR_TYPE.CONNECTION_LOST,
    1035: ERROR_TYPE.CONNECTION_LOST,
    1062: ERROR_TYPE.CONNECTION_LOST,
    4005: ERROR_TYPE.CONNECTION_LOST,
    1014: ERROR_TYPE.TOO_MANY_GAMES_OPEN,
    1024: ERROR_TYPE.TOO_MANY_GAMES_OPEN,
    1612: ERROR_TYPE.FREE_SPIN_DIALOG_START,
    1613: ERROR_TYPE.FREE_SPIN_DIALOG_FINISH,
    1614: ERROR_TYPE.FREE_SPIN_DIALOG_FINISH,
    1615: ERROR_TYPE.FREE_SPIN_DIALOG_FINISH,
    1616: ERROR_TYPE.FREE_SPIN_DIALOG_FINISH,
    9010: ERROR_TYPE.FREE_SPIN_DIALOG_CHOOSE,
  },
  LNW: {
    1006: ERROR_TYPE.INSUFFICIENT_FUNDS,
    1000: ERROR_TYPE.SESSION_EXPIRED,
    1003: ERROR_TYPE.SESSION_EXPIRED,
    CLIENT_INSUFFICIENT_FUNDS: ERROR_TYPE.INSUFFICIENT_FUNDS,
    UNKNOWN: ERROR_TYPE.UNKNOWN,
  },
  EVERI: { INSUFFICIENT_FUND: ERROR_TYPE.INSUFFICIENT_FUNDS },
  EVOLUTION: {
    lowBalance: ERROR_TYPE.INSUFFICIENT_FUNDS,
  },
  ARISTOCRAT: {
    [errorsRefs.INSUFFICIENT_FUNDS]: ERROR_TYPE.INSUFFICIENT_FUNDS,
    [errorsRefs.TIME_LIMIT_EXCEEDED]: ERROR_TYPE.SESSION_EXPIRED,
    [errorsRefs.GENERIC_ERROR]: ERROR_TYPE.GENERIC,
    [errorsRefs.DISCONNECTED]: ERROR_TYPE.CONNECTION_LOST,
    [errorsRefs.LOCATION_FORBIDDEN]: ERROR_TYPE.UNKNOWN,
    [errorsRefs.NOT_LOGGED_IN]: ERROR_TYPE.SESSION_EXPIRED,
    [errorsRefs.PLAY_FOR_REAL]: ERROR_TYPE.UNKNOWN,
    [errorsRefs.LOSS_LIMIT]: ERROR_TYPE.GENERIC,
    [errorsRefs.TIME_LIMIT]: ERROR_TYPE.GENERIC,
    [errorsRefs.TURNOVER_LIMIT]: ERROR_TYPE.GENERIC,
    [errorsRefs.LIFETIME_DEPOSIT]: ERROR_TYPE.GENERIC,
    [errorsRefs.DEPOSIT_LIMIT]: ERROR_TYPE.GENERIC,
  },
  PLAYTECH: {
    GENERIC_ERROR: ERROR_TYPE.GENERIC,
    INSUFFICIENT_FUNDS: ERROR_TYPE.INSUFFICIENT_FUNDS,
    AUTHENTICATION_ERROR: ERROR_TYPE.SESSION_EXPIRED,
    CONNECTION_ERROR: ERROR_TYPE.CONNECTION_LOST,
  },
  PLAYTECH_POKER: {
    GENERIC_ERROR: ERROR_TYPE.GENERIC,
    INSUFFICIENT_FUNDS: ERROR_TYPE.INSUFFICIENT_FUNDS,
    AUTHENTICATION_ERROR: ERROR_TYPE.SESSION_EXPIRED,
    CONNECTION_ERROR: ERROR_TYPE.CONNECTION_LOST,
  },
  DGC: {
    [dgcErrorRefs.INSUFFICIENT_FUNDS]: ERROR_TYPE.INSUFFICIENT_FUNDS,
    [dgcErrorRefs.CONNECTION_ERROR]: ERROR_TYPE.CONNECTION_LOST,
    [dgcErrorRefs.GENERIC_ERROR]: ERROR_TYPE.GENERIC,
    [dgcErrorRefs.AUTHENTICATION_ERROR]: ERROR_TYPE.SESSION_EXPIRED,
    [dgcErrorRefs.WAGER_LIMIT]: ERROR_TYPE.GENERIC,
    [dgcErrorRefs.TOO_MANY_GAMES_OPEN]: ERROR_TYPE.TOO_MANY_GAMES_OPEN,
    [dgcErrorRefs.LOSS_LIMIT]: ERROR_TYPE.GENERIC,
  },
  AWAGER: {
    limitReached: ERROR_TYPE.GENERIC,
    criticalError: ERROR_TYPE.GENERIC,
    sessionExpired: ERROR_TYPE.SESSION_EXPIRED,
  },
  PRAGMATIC: {
    [errorRefsPragmatic.INSUFFICIENT_FUNDS]: ERROR_TYPE.INSUFFICIENT_FUNDS,
    [errorRefsPragmatic.AUTHENTICATION_ERROR]: ERROR_TYPE.SESSION_EXPIRED,
    [errorRefsPragmatic.NETWORK_ERROR]: ERROR_TYPE.CONNECTION_LOST,
    [errorRefsPragmatic.GENERIC_ERROR]: ERROR_TYPE.GENERIC,
    [errorRefsPragmatic.SESSION_EXPIRED]: ERROR_TYPE.SESSION_EXPIRED,
    [errorRefsPragmatic.LIMIT_REACHED]: ERROR_TYPE.GENERIC,
    [errorRefsPragmatic.BET_NOT_ALLOWED]: ERROR_TYPE.GENERIC,
    [errorRefsPragmatic.BET_WITHOUT_CONFIGURED_JACKPOT]: ERROR_TYPE.GENERIC,
    [errorRefsPragmatic.REQUEST_ERROR]: ERROR_TYPE.GENERIC,
  },
  FUSION: {
    2: ERROR_TYPE.SESSION_EXPIRED,
    4: ERROR_TYPE.INSUFFICIENT_FUNDS,
    3: ERROR_TYPE.GENERIC, // Bet was not placed
    5: ERROR_TYPE.CONNECTION_LOST, // 504 on Gateway
    15: ERROR_TYPE.GENERIC,
    16: ERROR_TYPE.UNKNOWN,
    97: ERROR_TYPE.GENERIC,
  },
};

export const PROVIDER_ERROR_MESSAGES = {
  AWAGER: {
    limitReached: 'The player has reached their wager limit.',
    criticalError:
      "We're sorry about this. Please try again or leave your game to go home.",
    sessionExpired: 'Dismiss this message to re-launch the game.',
    betLimit: 'User has hit their maximum bet limit.',
    timeLimit: 'User has hit their time limit.',
    wagerLimit: 'User has hit their wager limit.',
    lossLimit: 'User has hit their loss limit.',
  },
  PRAGMATIC: {
    [errorRefsPragmatic.LIMIT_REACHED]:
      'The player has reached their wager limit.',
  },
  FUSION: {
    15: 'User has hit their wager limit.',
    16: 'User has hit their loss limit.',
    97: 'User has hit their wager limit.',
  },
};

export const getErrorType = (
  code: string | number,
  provider: string,
  message?: boolean,
  payload?: unknown,
): string => {
  if (message) {
    return PROVIDER_ERROR_MESSAGES[provider]?.[code] || '';
  }

  const isMapped = !!(
    PROVIDER_ERRORS[provider] && PROVIDER_ERRORS[provider][code]
  );
  const errorType = isMapped
    ? PROVIDER_ERRORS[provider][code]
    : ERROR_TYPE.GENERIC;

  if (!isMapped && payload) {
    handleUnknownMapping(payload, code);
  }

  return errorType;
};

const PROVIDER_ERROR_CATEGORIES: Partial<
  Record<string, Record<string, string>>
> = {
  LNW: {
    CONNECTION_ERROR: ERROR_TYPE.SESSION_EXPIRED,
  },
};

export const getErrorTypeByCategory = (
  category: unknown,
  provider: string,
): string | undefined =>
  typeof category === 'string'
    ? PROVIDER_ERROR_CATEGORIES[provider]?.[category]
    : undefined;

export const isErrorCodeMapped = (code: string, provider: string): boolean =>
  !!(PROVIDER_ERRORS[provider] && PROVIDER_ERRORS[provider][code]);

export const getMatureErrorMessage = (code: string, errorMessage: string) => {
  const isUnknownErrorCode = code === 'UNKNOWN';
  const isMatureLanguageErrorMessage = errorMessage.includes('adult language');

  if (!isUnknownErrorCode) return errorMessage;
  return isMatureLanguageErrorMessage ? errorMessage : '';
};
